import { ActionFunction, redirect } from 'react-router-dom';
import { saveData } from './Action/saveData';

// Sentry
import * as Sentry from '@sentry/react';

// Redux - Para marcar la tab como visitada
import { store } from '../../../../../state/configureStore';
import { actionTabs } from '../../../../../state/actions/ui/actionTabs';

// Validación centralizada
import {
  validateAllFormsFromSessionStorage,
  generateValidationSummary,
} from '../../../../../utils/formValidation';

// Utils
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';

// Constants
import { formActionNaming } from '../../../../../components/forms/ActionForms/constants';

// Offline / avisos
import { isOfflineQueuedError } from '../../../../../offline/isOfflineQueuedError';
import { showSuccessToast, showBackendErrorToast } from '../../../../../utils/toasts';
import i18n from '../../../../../i18n';

/**
 * Lee `orderParentId` y `isCampaignParent` del sessionStorage del wizard sin
 * fallar si está vacío.
 */
function readWizardParent(): {
  orderParentId: string | undefined;
  isCampaignParent: boolean;
} {
  try {
    const raw = sessionStorage.getItem(formActionNaming.NEW_ACTION_DATA);
    if (!raw) return { orderParentId: undefined, isCampaignParent: false };
    const data = JSON.parse(decompressSessionStorage(raw)) as {
      orderParentId?: string;
      isCampaignParent?: boolean;
    };
    return {
      orderParentId: data.orderParentId,
      isCampaignParent: !!data.isCampaignParent,
    };
  } catch {
    return { orderParentId: undefined, isCampaignParent: false };
  }
}

export const AnimalsAction: ActionFunction = async () => {
  try {
    // Marcar la tab como visitada ANTES de validar/guardar
    store.dispatch(actionTabs('animals'));

    // ═══════════════════════════════════════════════════════════════
    // VALIDACIÓN CENTRALIZADA: Validar TODAS las tabs antes de guardar
    // ═══════════════════════════════════════════════════════════════
    console.log('🔍 [AnimalsAction] Iniciando validación centralizada...');

    // Feature flag showHoursType - valor por defecto false
    // (ActionFunction no puede usar React hooks para obtener el valor dinámico)
    // NOTA: La mayoría de tenants tienen este flag en false, haciendo hoursType opcional
    const showHoursType = false;

    const validationResult = await validateAllFormsFromSessionStorage({
      isNewAction: true,
      showHoursType,
      // SR/FR-9.3 (2026-07-04): en el wizard del RV la tab Operarios está
      // oculta — operarios/vehículos heredados de la ruta en el backend.
      skipOperatorsValidation: window.location.pathname.startsWith(
        '/surveillance-route/',
      ),
    });

    console.log('🔍 [AnimalsAction] Resultado de validación:', {
      isValid: validationResult.isValid,
      errorsCount: validationResult.errors.length,
      tabsWithErrors: validationResult.tabsWithErrors.map((t) => t.tabKey),
    });

    // Si hay errores de validación, retornar para mostrar en el componente
    if (!validationResult.isValid) {
      console.log('❌ [AnimalsAction] Validación fallida, retornando errores');

      return {
        validationFailed: true,
        validationErrors: validationResult.errors,
        tabsWithErrors: validationResult.tabsWithErrors,
        validationSummary: generateValidationSummary(
          validationResult.tabsWithErrors
        ),
      };
    }

    // ═══════════════════════════════════════════════════════════════
    // Si la validación pasa, proceder a guardar
    // ═══════════════════════════════════════════════════════════════
    console.log('✅ [AnimalsAction] Validación exitosa, guardando actuación...');

    // C-11.9: `await` para que el catch atrape rejections async (offlineQueued).
    return await saveData();
  } catch (err) {
    if (isOfflineQueuedError(err)) {
      // La fase 2 se encoló (sin red, o con red degradada tras el plazo): éxito
      // diferido. El destino lo pone quien encoló —la básica vuelve al
      // recorrido—; si no viene, el padre del borrador.
      showSuccessToast(i18n.t('modals:changeState.queuedOffline'));
      const { orderParentId, isCampaignParent } = readWizardParent();
      Object.values(formActionNaming).forEach((key) =>
        sessionStorage.removeItem(key),
      );
      const fallback = `${isCampaignParent ? '/campaigns' : '/work-orders'}/${orderParentId ?? ''}`;
      return redirect((err as { redirectTo?: string }).redirectTo ?? fallback);
    }
    // Negocio (400, validación del backend…): antes se tragaba y el asistente
    // redirigía al resumen como si hubiera guardado. Ahora se avisa con el
    // mensaje del backend y el asistente se queda, con el borrador intacto.
    Sentry.captureException(err);
    console.error('❌ [AnimalsAction] Error:', err);
    showBackendErrorToast(err, i18n.t('toasts:error.generic'));
    return {
      error: true,
      errorMessage: 'Error al guardar la actuación',
    };
  }
};
